import { useMemo } from "react";

import { useNotificationContext } from "../context/NotificationContext";

interface QuoteQAAnswerNotificationsData {
  notificationsByQuote: Map<
    number,
    { count: number; notificationIds: number[] }
  >;
}

interface Props {
  quoteIds: number[];
}

interface QuoteNotificationMetadata {
  quote_id: number;
}

const QA_ANSWER_NOTIFICATION_TYPES = [
  "SHIPPER_QUOTE_QUESTION_ANSWERED", // When carrier answers shipper's question
];

// Limitation: the notification context holds at most the 100 most recent
// notifications, ordered unread first. Quote-card badges miss unread QA-answer
// notifications only for users with more than 100 unread notifications.
// Accepted as a rare, cosmetic case.
export function useQuoteQAAnswerNotifications(
  props: Props,
): QuoteQAAnswerNotificationsData & {
  loading: boolean;
  error: string | null;
} {
  const { quoteIds } = props;
  const { notifications, loading } = useNotificationContext();

  const notificationsByQuote = useMemo(() => {
    const result = new Map<
      number,
      { count: number; notificationIds: number[] }
    >();

    // Initialize all quote IDs with empty results
    quoteIds.forEach((quoteId) => {
      result.set(quoteId, { count: 0, notificationIds: [] });
    });

    if (quoteIds.length === 0) {
      return result;
    }

    const allNotifications = notifications || [];

    // Filter for QA answer notifications that are unread
    const qaAnswerNotifications = allNotifications.filter(
      (notification: Notification) => {
        if (!QA_ANSWER_NOTIFICATION_TYPES.includes(notification.type)) {
          return false;
        }

        if (notification.read_at !== null) {
          return false;
        }

        const metadata = notification.metadata as QuoteNotificationMetadata;
        return metadata?.quote_id && quoteIds.includes(metadata.quote_id);
      },
    );

    // Group notifications by quote ID
    qaAnswerNotifications.forEach((notification: Notification) => {
      const metadata = notification.metadata as QuoteNotificationMetadata;
      const quoteId = metadata.quote_id;

      if (quoteId) {
        const existing = result.get(quoteId) ?? {
          count: 0,
          notificationIds: [],
        };
        existing.count += 1;
        existing.notificationIds.push(notification.id);
        result.set(quoteId, existing);
      }
    });

    return result;
  }, [notifications, quoteIds]);

  return {
    notificationsByQuote,
    loading,
    error: null, // Context handles errors, but we need to maintain interface
  };
}
